/**
 * ConvertDemo (iter-208 · Fase B migration to useZodForm)
 *
 * Authenticated route /convert. Visible only to users whose
 * /auth/me returns ``is_demo: true``. Lets them claim their demo
 * data as a real account by setting a real email + password.
 * Flow unchanged from iter-202.
 */
import { useEffect, useRef } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../../components/ui/card";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { api, tokenStore } from "../../lib/api";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { ShieldCheck, Sparkles, Clock } from "lucide-react";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";

export default function ConvertDemo() {
  const { user, loading: authLoading, refresh: refreshUser } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const prefilledRef = useRef(false);

  const Schema = z.object({
    email: z.string().trim().min(1, tx(lang, "Email is required.", "El email es obligatorio.", "O e-mail é obrigatório."))
      .email(tx(lang, "Invalid email.", "Email inválido.", "E-mail inválido.")),
    password: z.string().min(6, tx(lang, "Min 6 characters.", "Mínimo 6 caracteres.", "Mínimo 6 caracteres.")),
    confirmPassword: z.string().min(1, tx(lang, "Confirm the password.", "Confirma la contraseña.", "Confirme a senha.")),
  }).refine((d) => d.password === d.confirmPassword, {
    message: tx(lang, "Passwords don't match.", "Las contraseñas no coinciden.", "As senhas não coincidem."),
    path: ["confirmPassword"],
  });

  const form = useZodForm(Schema, { email: "", password: "", confirmPassword: "" }, { formId: "convert-demo" });

  useEffect(() => {
    if (!prefilledRef.current && user?.demo_request_email) {
      prefilledRef.current = true;
      form.setValue("email", user.demo_request_email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.demo_request_email]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="convert-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/register" replace />;
  if (!user.is_demo) return <Navigate to="/dashboard" replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const r = await api.post("/demo/convert", {
        email: values.email.trim().toLowerCase(),
        password: values.password,
        language: lang,
      });
      if (r.data?.access_token) tokenStore.set(r.data.access_token);
      toast.success(tx(lang,
        "Demo converted to a real account.",
        "Tu demo se convirtió en cuenta real.",
        "Seu demo virou conta real."));
      if (refreshUser) await refreshUser();
      window.location.assign("/dashboard");
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      let msg;
      if (code === 409 || detail === "email_taken") {
        msg = tx(lang,
          "That email is already used by another Lumfi account. Use a different one or sign in.",
          "Ese email ya pertenece a otra cuenta de Lumfi. Usa otro o inicia sesión.",
          "Esse email já pertence a outra conta. Use outro ou faça login.");
        form.setError("email", msg);
      } else if (code === 400) {
        msg = tx(lang,
          "Couldn't convert your demo. Check the form and try again.",
          "No pudimos convertir tu demo. Revisa el formulario.",
          "Não conseguimos converter o demo.");
      } else {
        msg = tx(lang,
          "Network error. Try again in a moment.",
          "Error de red. Prueba en un momento.",
          "Erro de rede. Tente novamente.");
      }
      throw new Error(msg);
    }
  }, {
    onError: ({ message }) => message && toast.error(message),
  });

  const pendingPlan = user.demo_plan;
  const pendingPacks = user.demo_packs || [];
  const pendingAddons = user.demo_addons || [];
  const hasAdminOffer = pendingPlan || pendingPacks.length || pendingAddons.length;

  return (
    <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4 py-10" data-testid="convert-demo-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <LumfiWordmark height={36} className="mx-auto" />
          <h1 className="font-serif-display text-3xl mt-7 text-slate-900">
            {tx(lang, "Keep my demo data", "Conservar mis datos del demo", "Manter meus dados do demo")}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {tx(lang,
              "Set a password to turn your demo into a permanent Lumfi account.",
              "Define una contraseña para convertir tu demo en una cuenta Lumfi permanente.",
              "Defina uma senha para virar conta Lumfi permanente.")}
          </p>
        </div>

        <Card className="p-6 sm:p-7 shadow-sm border-stone-200">
          {hasAdminOffer && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 mb-5" data-testid="convert-admin-offer">
              <div className="flex items-start gap-2.5">
                <Sparkles className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-xs text-emerald-900 leading-snug">
                  <p className="font-semibold mb-1">
                    {tx(lang, "Offer reserved during your demo", "Oferta reservada en tu demo", "Oferta reservada no seu demo")}
                  </p>
                  <div className="space-y-0.5">
                    {pendingPlan && <p>Plan: <strong>{pendingPlan}</strong></p>}
                    {pendingPacks.length > 0 && <p>Packs: <strong>{pendingPacks.join(", ")}</strong></p>}
                    {pendingAddons.length > 0 && <p>Add-ons: <strong>{pendingAddons.join(", ")}</strong></p>}
                  </div>
                  <p className="mt-1.5 text-[11px] text-emerald-800 opacity-90">
                    {tx(lang,
                      "Activate later from My plan — no charge today.",
                      "Lo activas más tarde desde Mi plan — hoy no hay cobro.",
                      "Ative depois em Meu plano — hoje sem cobrança.")}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" data-testid="convert-form" noValidate>
            <FormField
              label={tx(lang, "Real email", "Email real", "Email real")}
              name="email"
              required
              error={form.errors.email && (form.touched.email || form.submitCount > 0) ? form.errors.email : undefined}
            >
              <FormField.Input
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={tx(lang, "you@work.com", "tu@trabajo.com", "voce@trabalho.com")}
                data-testid="convert-email-input"
                className="h-11"
                {...form.getFieldProps("email")}
              />
            </FormField>

            <FormField
              label={tx(lang, "Password", "Contraseña", "Senha")}
              name="password"
              required
              error={form.errors.password && (form.touched.password || form.submitCount > 0) ? form.errors.password : undefined}
            >
              <FormField.Input
                type="password"
                autoComplete="new-password"
                placeholder={tx(lang, "At least 6 characters", "Al menos 6 caracteres", "Mínimo 6 caracteres")}
                data-testid="convert-password-input"
                className="h-11"
                {...form.getFieldProps("password")}
              />
            </FormField>

            <FormField
              label={tx(lang, "Confirm password", "Confirma la contraseña", "Confirme a senha")}
              name="confirmPassword"
              required
              error={form.errors.confirmPassword && (form.touched.confirmPassword || form.submitCount > 0) ? form.errors.confirmPassword : undefined}
            >
              <FormField.Input
                type="password"
                autoComplete="new-password"
                data-testid="convert-password-confirm"
                className="h-11"
                {...form.getFieldProps("confirmPassword")}
              />
            </FormField>

            <SubmitButton
              submitting={form.submitting}
              loadingText={tx(lang, "Saving…", "Guardando…", "Salvando…")}
              testid="convert-submit"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base rounded-full"
            >
              <ShieldCheck className="h-4 w-4" />
              {tx(lang, "Keep my data", "Conservar mis datos", "Manter meus dados")}
            </SubmitButton>
          </form>

          <p className="text-[11px] text-slate-500 mt-4 leading-snug flex items-start gap-1.5">
            <Clock className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              {tx(lang,
                "Once converted, you keep all data from the demo. You stay on the Free plan until you activate the offer above.",
                "Una vez convertido, mantienes todos los datos del demo. Quedas en plan Free hasta que actives la oferta de arriba.",
                "Após converter, mantém todos os dados. Fica no plano Free até ativar a oferta acima.")}
            </span>
          </p>
        </Card>

        <div className="text-center text-xs text-slate-500">
          <Link to="/dashboard" className="hover:text-slate-900" data-testid="convert-back-dashboard">
            ← {tx(lang, "Back to the demo", "Volver al demo", "Voltar ao demo")}
          </Link>
        </div>
      </div>
    </div>
  );
}
